import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { comics, comicCategories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'korean-manhwa');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const category = comicCategories.find((c) => c.slug === activeCategory);
  const categoryComics = comics.filter((c) => c.category === activeCategory);

  const catName = category?.name || '漫画分类';
  const catDesc = `汗汗漫画${catName}专区，收录大量优质${catName}资源，涵盖热门连载与经典完结作品，每日更新，免费在线阅读。`;

  return (
    <>
      <SEOHead
        title={`${catName} - 汗汗漫画`}
        description={catDesc}
        keywords={`汗汗漫画,${catName},好看的韩国漫画大全,虫虫漫画,免费漫画`}
        canonicalUrl={`/category/${activeCategory}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: catName },
                ]}
              />

              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-2 mb-6">
                {catName}
              </h1>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 flex-nowrap">
                {comicCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-primary-600'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Comics Grid */}
              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                  {categoryComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="group cursor-pointer"
                      data-product-shop
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          title={`${comic.title} - ${catName}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                          {comic.rating}分
                        </div>
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-background-950/70 text-background-50 text-xs whitespace-nowrap">
                          {comic.status}
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                        {comic.author}
                      </p>
                      <p className="text-xs text-foreground-400 mt-0.5 whitespace-nowrap">
                        共{comic.chapters.length}话 · {comic.views.toLocaleString()}阅读
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </span>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画，请查看其他分类</p>
                  <Link
                    to="/category/korean-manhwa"
                    className="inline-block mt-4 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    查看韩国漫画
                  </Link>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}